import React, { useEffect } from 'react';
import {
  Drawer,
  Button,
  Typography,
  Space,
  InputNumber,
  Image,
  Alert,
  Empty,
  Spin,
  Divider,
  Popconfirm,
  Badge,
  message,
  Grid,
} from 'antd';
import { DeleteOutlined, ShoppingCartOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useCartStore } from '../../stores/cartStore';
import { CartItem } from '../../services/cartService';
import { ProductService } from '../../services/productService';

const { Text } = Typography;

interface CartDrawerProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Side cart panel (issue #121).
 *
 * The header's Cart used to navigate to /cart, which unmounted the product list
 * and dropped the shopper back on page 1 when they came back. Reviewing the cart
 * in a drawer leaves the route — and so the list's page, search and category —
 * untouched, so closing the panel puts them exactly where they were.
 *
 * The full /cart page stays as it is: still a shareable URL, still where
 * "View full cart" leads.
 */
const CartDrawer: React.FC<CartDrawerProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const {
    items,
    summary,
    loading,
    error,
    fetchCart,
    updateCartItem,
    removeFromCart,
    clearError,
  } = useCartStore();

  // Refetch on each open: prices and availability can move while the shopper
  // browses, and the badge count alone does not prove the lines are current.
  useEffect(() => {
    if (open) fetchCart();
  }, [open, fetchCart]);

  const handleQuantityChange = async (supersedeNoUOM: string, quantity: number) => {
    try {
      await updateCartItem(supersedeNoUOM, quantity);
    } catch {
      message.error('Failed to update quantity');
    }
  };

  const handleRemoveItem = async (item: CartItem) => {
    try {
      await removeFromCart(item.SupersedeNoUOM);
      message.success(`Removed ${item.Description} from cart`);
    } catch {
      message.error('Failed to remove item');
    }
  };

  /** Leaving the panel for a route: always close, so it cannot cover the page. */
  const goTo = (path: string) => {
    onClose();
    navigate(path);
  };

  const currency = summary?.calculation.currencyCodeStr ?? '$';
  const grandTotal = summary ? summary.calculation.gTotalD || summary.calculation.totalD : 0;

  const renderItems = () => (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {items.map((record) => (
        <div key={record.SupersedeNoUOM}>
          <div style={{ display: 'flex', gap: 12 }}>
            <Image
              width={64}
              height={64}
              src={ProductService.getProductImageUrl(record.MainPictureFull)}
              fallback="/images/product-placeholder.svg"
              preview={false}
              style={{ borderRadius: 8, objectFit: 'cover', flexShrink: 0 }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <Text strong>{record.Description}</Text>
              <br />
              <Text type="secondary" style={{ fontSize: '0.85em' }}>
                {record.UOM} &bull; {record.SupersedeNo}
              </Text>
              <div style={{ marginTop: 4 }}>
                <Text style={{ color: 'var(--trust-blue)' }}>{record.PriceDesc1}</Text>
              </div>
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
              marginTop: 10,
            }}
          >
            <Space size="small">
              <Text type="secondary">Qty</Text>
              <InputNumber
                min={1}
                value={record.Qty}
                onChange={(value) => {
                  if (value && value > 0) handleQuantityChange(record.SupersedeNoUOM, value);
                }}
                style={{ width: 72 }}
              />
            </Space>
            <Text strong style={{ color: 'var(--trust-blue)' }}>
              ${record.TotalPrice.toFixed(2)}
            </Text>
            <Popconfirm
              title="Remove item"
              description="Remove this item from your cart?"
              onConfirm={() => handleRemoveItem(record)}
              okText="Remove"
              cancelText="Cancel"
            >
              <Button type="text" danger icon={<DeleteOutlined />} loading={loading} />
            </Popconfirm>
          </div>

          <Divider style={{ margin: '12px 0 0' }} />
        </div>
      ))}
    </Space>
  );

  const summaryRow = (label: React.ReactNode, value: React.ReactNode, strong = false) => (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <Text strong={strong}>{label}</Text>
      <Text strong={strong}>{value}</Text>
    </div>
  );

  return (
    <Drawer
      title={
        <Space>
          <ShoppingCartOutlined />
          <span>Your Cart</span>
          {items.length > 0 && <Badge count={items.length} />}
        </Space>
      }
      placement="right"
      onClose={onClose}
      open={open}
      // Full width on phones; a fixed panel on anything larger, so the product
      // list stays visible behind it.
      width={screens.sm ? 420 : '100%'}
      // rootClassName, not className: antd puts `className` on the inner panel,
      // and the whole drawer (mask included) needs to be addressable.
      rootClassName="dasoon-cart-drawer"
      footer={
        items.length > 0 ? (
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            {summary && (
              <>
                {summaryRow(
                  summary.calculation.totalStr || 'Subtotal:',
                  `${currency}${summary.calculation.totalD.toFixed(2)}`
                )}
                {summary.calculation.deliveryAmtD > 0 &&
                  summaryRow(
                    summary.calculation.deliveryAmtStr,
                    `${currency}${summary.calculation.deliveryAmtD.toFixed(2)}`
                  )}
                {summary.calculation.gstAmtD > 0 &&
                  summaryRow(
                    summary.calculation.gstAmtStr,
                    `${currency}${summary.calculation.gstAmtD.toFixed(2)}`
                  )}
                <Divider style={{ margin: '8px 0' }} />
                {summaryRow(
                  summary.calculation.gTotalStr || `Total ${currency}:`,
                  <span style={{ color: 'var(--dasoon-primary)', fontSize: '1.1em' }}>
                    {currency}
                    {grandTotal.toFixed(2)}
                  </span>,
                  true
                )}
              </>
            )}
            <Button
              type="primary"
              className="dasoon-btn-primary"
              size="large"
              style={{ width: '100%', marginTop: 8 }}
              onClick={() => goTo('/checkout')}
            >
              Proceed to Checkout
            </Button>
            <Button type="default" style={{ width: '100%' }} onClick={() => goTo('/cart')}>
              View full cart
            </Button>
            {/* Closing is the point of the panel: it returns the shopper to the
                page, and the page number, they were already on. */}
            <Button type="text" style={{ width: '100%' }} onClick={onClose}>
              Continue Shopping
            </Button>
          </Space>
        ) : null
      }
    >
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          closable
          onClose={clearError}
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {loading && items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 0' }}>
          <Spin size="large" />
        </div>
      ) : items.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={<Text type="secondary">Your cart is empty</Text>}
          style={{ marginTop: 48 }}
        >
          <Button type="primary" onClick={() => goTo('/products')}>
            Browse Products
          </Button>
        </Empty>
      ) : (
        renderItems()
      )}
    </Drawer>
  );
};

export default CartDrawer;
